import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Search, ShoppingCart, User, Menu, LogOut, Package, Heart, MessageCircle, Bell, Settings, MessageSquare, X, Compass, Store } from 'lucide-react';
import { useAuthStore, useCartStore, useThemeStore } from '@/store';
import { useLogout } from '@/hooks/useApi';
import { useState, useEffect } from 'react';
import UserAvatar from '@/components/UserAvatar';

export default function Header() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { isAuthenticated, user } = useAuthStore();
  const getTotalItems = useCartStore((state) => state.getTotalItems);
  const { isDark } = useThemeStore();
  const logo = isDark ? '/images/logo_night.png' : '/images/logo.png';
  const { mutate: logout } = useLogout();
  const [showUserMenu, setShowUserMenu]     = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [searchQuery, setSearchQuery]       = useState('');
  const [unreadCount, setUnreadCount]       = useState(0);
  const [notifCount, setNotifCount]         = useState(0);

  const cartCount = getTotalItems();

  // Close mobile menu on route change
  useEffect(() => { setShowMobileMenu(false); }, [pathname]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const fetchUnread = async () => {
      try {
        const { apiClient } = await import('@/lib/api-client');
        const response = await apiClient.get<any>('/messages/unread-count');
        setUnreadCount(response.data?.count ?? 0);
      } catch {}
    };
    const fetchNotifCount = async () => {
      try {
        const { apiClient } = await import('@/lib/api-client');
        const response = await apiClient.get<any>('/notifications/unread-count');
        setNotifCount(response.data?.data?.count ?? 0);
      } catch {}
    };
    fetchUnread();
    fetchNotifCount();
    const interval = setInterval(() => { fetchUnread(); fetchNotifCount(); }, 60000);
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/explore?q=${encodeURIComponent(searchQuery)}`);
      setShowMobileMenu(false);
    }
  };

  const handleLogout = () => {
    useAuthStore.getState().logout();
    logout();
    setShowUserMenu(false);
    setShowMobileMenu(false);
    navigate('/');
  };

  return (
    <>
      <header className="sticky top-0 z-50 w-full border-b border-gray-200 dark:border-zinc-800 bg-gray-50/95 dark:bg-zinc-900/95 backdrop-blur">
        <div className="container-custom">
          <div className="flex h-20 items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <img src={logo} alt="Open Card Vault" className="h-[72px] w-auto" />
            </Link>

            {/* Search Bar — desktop */}
            <form onSubmit={handleSearch} className="hidden md:flex flex-1 max-w-md mx-8">
              <div className="relative w-full">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 dark:text-zinc-500" />
                <input
                  type="text"
                  placeholder="Search cards..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="input pl-10 pr-4"
                />
              </div>
            </form>

            {/* Navigation */}
            <nav className="flex items-center space-x-1 sm:space-x-2">
              <div className="hidden lg:flex items-center space-x-6 mr-4">
                <Link to="/explore" className="text-sm font-medium text-gray-700 dark:text-zinc-300 hover:text-brand-600 dark:hover:text-brand-400 transition-colors">Explore</Link>
                <Link to="/marketplace" className="text-sm font-medium text-gray-700 dark:text-zinc-300 hover:text-brand-600 dark:hover:text-brand-400 transition-colors">Marketplace</Link>
                <Link to="/forum" className="text-sm font-medium text-gray-700 dark:text-zinc-300 hover:text-brand-600 dark:hover:text-brand-400 transition-colors">Forum</Link>
              </div>

              {/* Notifications */}
              {isAuthenticated && (
                <Link to="/notifications" aria-label="Notifications" className="relative inline-flex items-center justify-center h-11 w-11">
                  <Bell className={`h-6 w-6 transition-colors ${notifCount > 0 ? 'text-brand-600 dark:text-brand-400 fill-brand-400' : 'text-gray-700 dark:text-zinc-300 hover:text-brand-600 dark:hover:text-brand-400'}`} />
                  {notifCount > 0 && (
                    <span className="absolute top-1.5 right-1.5 flex items-center justify-center text-white font-bold" style={{ fontSize: notifCount > 9 ? '7px' : '9px', paddingTop: '2px' }}>
                      {notifCount > 99 ? '99+' : notifCount}
                    </span>
                  )}
                </Link>
              )}

              {/* Messages — hidden on mobile (covered by bottom nav) */}
              {isAuthenticated && (
                <Link to="/messages" aria-label="Messages" className="relative hidden sm:inline-flex items-center justify-center h-11 w-11">
                  <MessageCircle className={`h-6 w-6 transition-colors ${unreadCount > 0 ? 'text-brand-600 dark:text-brand-400 fill-brand-400' : 'text-gray-700 dark:text-zinc-300 hover:text-brand-600 dark:hover:text-brand-400'}`} />
                  {unreadCount > 0 && (
                    <span className="absolute top-1.5 right-1.5 flex items-center justify-center text-white font-bold" style={{ fontSize: unreadCount > 9 ? '7px' : '9px', paddingTop: '2px' }}>
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </Link>
              )}

              {/* Cart */}
              <Link to="/cart" aria-label="Shopping cart" className="relative inline-flex items-center justify-center h-11 w-11">
                <ShoppingCart className="h-5 w-5 text-gray-700 dark:text-zinc-300 hover:text-brand-600 dark:hover:text-brand-400 transition-colors" />
                {cartCount > 0 && (
                  <span className="absolute top-0.5 right-0.5 h-5 w-5 rounded-full bg-brand-600 text-white text-xs flex items-center justify-center font-medium">
                    {cartCount}
                  </span>
                )}
              </Link>

              {/* User Menu — desktop */}
              {isAuthenticated && user ? (
                <div className="relative hidden sm:block">
                  <button onClick={() => setShowUserMenu(!showUserMenu)} className="flex items-center space-x-2 hover:opacity-80 transition-opacity">
                    <UserAvatar username={user.username} avatarUrl={user.avatar_url} size="sm" />
                    <span className="hidden lg:inline text-sm font-medium text-gray-700 dark:text-zinc-300">{user.username}</span>
                  </button>

                  {showUserMenu && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setShowUserMenu(false)} />
                      <div className="absolute right-0 mt-2 w-48 rounded-lg bg-white dark:bg-zinc-900 border border-gray-300 dark:border-zinc-700 shadow-lg dark:shadow-black/40 py-1 z-20">
                        <Link to="/dashboard" className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-zinc-300 hover:bg-gray-100 dark:hover:bg-zinc-800" onClick={() => setShowUserMenu(false)}>
                          <User className="h-4 w-4 mr-2" />Dashboard
                        </Link>
                        <Link to="/collection" className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-zinc-300 hover:bg-gray-100 dark:hover:bg-zinc-800" onClick={() => setShowUserMenu(false)}>
                          <Package className="h-4 w-4 mr-2" />Collection
                        </Link>
                        <Link to="/wishlist" className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-zinc-300 hover:bg-gray-100 dark:hover:bg-zinc-800" onClick={() => setShowUserMenu(false)}>
                          <Heart className="h-4 w-4 mr-2" />Wishlist
                        </Link>
                        <Link to="/notifications" className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-zinc-300 hover:bg-gray-100 dark:hover:bg-zinc-800" onClick={() => setShowUserMenu(false)}>
                          <Bell className="h-4 w-4 mr-2" />Notifications
                          {notifCount > 0 && <span className="ml-auto bg-brand-600 text-white text-xs rounded-full px-1.5 py-0.5">{notifCount}</span>}
                        </Link>
                        <Link to="/messages" className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-zinc-300 hover:bg-gray-100 dark:hover:bg-zinc-800" onClick={() => setShowUserMenu(false)}>
                          <MessageCircle className="h-4 w-4 mr-2" />Messages
                          {unreadCount > 0 && <span className="ml-auto bg-brand-600 text-white text-xs rounded-full px-1.5 py-0.5">{unreadCount}</span>}
                        </Link>
                        <Link to="/forum" className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-zinc-300 hover:bg-gray-100 dark:hover:bg-zinc-800" onClick={() => setShowUserMenu(false)}>
                          <MessageSquare className="h-4 w-4 mr-2" />Forum
                        </Link>
                        <Link to="/settings" className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-zinc-300 hover:bg-gray-100 dark:hover:bg-zinc-800" onClick={() => setShowUserMenu(false)}>
                          <Settings className="h-4 w-4 mr-2" />Settings
                        </Link>
                        <div className="border-t border-gray-200 dark:border-zinc-700 my-1" />
                        <button onClick={handleLogout} className="flex items-center w-full px-4 py-2 text-sm text-red-500 hover:bg-gray-100 dark:hover:bg-zinc-800">
                          <LogOut className="h-4 w-4 mr-2" />Logout
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div className="hidden sm:flex items-center space-x-3">
                  <Link to="/login" className="btn-ghost text-sm">Login</Link>
                  <Link to="/register" className="btn-primary text-sm">Sign Up</Link>
                </div>
              )}

              {/* Hamburger — mobile/tablet */}
              <button
                aria-label="Open menu"
                onClick={() => setShowMobileMenu(true)}
                className="lg:hidden inline-flex items-center justify-center h-11 w-11 rounded-lg hover:bg-gray-100 dark:hover:bg-zinc-800 transition-colors"
              >
                <Menu className="h-6 w-6 text-gray-700 dark:text-zinc-300" />
              </button>
            </nav>
          </div>
        </div>
      </header>

      {/* Mobile Menu Drawer */}
      {showMobileMenu && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowMobileMenu(false)}
          />

          {/* Drawer */}
          <div className="fixed top-0 right-0 z-50 h-full w-80 max-w-[85vw] bg-white dark:bg-zinc-900 shadow-2xl flex flex-col overflow-y-auto">
            {/* Drawer header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-zinc-800">
              <div className="flex items-center">
                <img src={logo} alt="Open Card Vault" className="h-[60px] w-auto" />
              </div>
              <button
                aria-label="Close"
                onClick={() => setShowMobileMenu(false)}
                className="h-9 w-9 flex items-center justify-center rounded-lg hover:bg-gray-100 dark:hover:bg-zinc-800 transition-colors"
              >
                <X className="h-5 w-5 text-gray-600 dark:text-zinc-400" />
              </button>
            </div>

            {/* Search */}
            <div className="px-4 py-3 border-b border-gray-100 dark:border-zinc-800">
              <form onSubmit={handleSearch}>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search cards..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="input pl-9 text-sm"
                  />
                </div>
              </form>
            </div>

            {/* User identity (if logged in) */}
            {isAuthenticated && user && (
              <div className="px-4 py-3 border-b border-gray-100 dark:border-zinc-800 flex items-center gap-3">
                <UserAvatar username={user.username} avatarUrl={user.avatar_url} size="sm" />
                <div>
                  <p className="font-semibold text-sm">{user.username}</p>
                  <p className="text-xs text-gray-500 dark:text-zinc-500">{user.email}</p>
                </div>
              </div>
            )}

            {/* Nav links */}
            <nav className="flex-1 px-2 py-3 space-y-1">
              <p className="px-3 py-1 text-xs font-semibold text-gray-400 dark:text-zinc-500 uppercase tracking-wider">Browse</p>
              <MobileNavLink to="/" icon={<Package className="h-5 w-5" />} label="Home" />
              <MobileNavLink to="/explore" icon={<Compass className="h-5 w-5" />} label="Explore Cards" />
              <MobileNavLink to="/marketplace" icon={<Store className="h-5 w-5" />} label="Marketplace" />
              <MobileNavLink to="/forum" icon={<MessageSquare className="h-5 w-5" />} label="Forum" />

              {isAuthenticated && (
                <>
                  <p className="px-3 pt-3 pb-1 text-xs font-semibold text-gray-400 dark:text-zinc-500 uppercase tracking-wider">My Account</p>
                  <MobileNavLink to="/dashboard" icon={<User className="h-5 w-5" />} label="Dashboard" />
                  <MobileNavLink to="/collection" icon={<Package className="h-5 w-5" />} label="Collection" />
                  <MobileNavLink to="/wishlist" icon={<Heart className="h-5 w-5" />} label="Wishlist" />
                  <MobileNavLink to="/messages" icon={<MessageCircle className="h-5 w-5" />} label="Messages" badge={unreadCount} />
                  <MobileNavLink to="/notifications" icon={<Bell className="h-5 w-5" />} label="Notifications" badge={notifCount} />
                  <MobileNavLink to="/settings" icon={<Settings className="h-5 w-5" />} label="Settings" />
                </>
              )}
            </nav>

            {/* Footer actions */}
            <div className="px-4 py-4 border-t border-gray-200 dark:border-zinc-800 pb-safe">
              {isAuthenticated ? (
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2 w-full px-4 py-2.5 rounded-xl text-sm font-medium text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                >
                  <LogOut className="h-5 w-5" /> Logout
                </button>
              ) : (
                <div className="flex flex-col gap-2">
                  <Link to="/login" className="btn-ghost w-full justify-center" onClick={() => setShowMobileMenu(false)}>Login</Link>
                  <Link to="/register" className="btn-primary w-full justify-center" onClick={() => setShowMobileMenu(false)}>Sign Up</Link>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
}

function MobileNavLink({ to, icon, label, badge }: { to: string; icon: React.ReactNode; label: string; badge?: number }) {
  return (
    <Link
      to={to}
      className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-gray-700 dark:text-zinc-300 hover:bg-gray-100 dark:hover:bg-zinc-800 transition-colors"
    >
      <span className="text-gray-500 dark:text-zinc-400">{icon}</span>
      <span className="flex-1">{label}</span>
      {badge != null && badge > 0 && (
        <span className="bg-brand-600 text-white text-xs font-bold rounded-full px-1.5 py-0.5 min-w-[1.25rem] text-center">
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </Link>
  );
}
